import { useEffect } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

interface ModalProps {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  className?: string;
  panelClassName?: string;
}

export function Modal({ children, open, onClose, className, panelClassName }: ModalProps) {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={clsx(
        "fixed inset-0 z-[999] flex items-center justify-center bg-black/45 p-4 backdrop-blur-md animate-fadeIn",
        className
      )}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={clsx(
          "max-h-[calc(100vh-32px)] w-full overflow-hidden rounded-[24px] bg-[var(--surface)] shadow-[0_28px_90px_rgba(17,17,17,0.26)] animate-modal-in",
          panelClassName
        )}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

interface FormModalInfoItem {
  icon?: React.ReactNode;
  label: string;
}

interface FormModalProps {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  eyebrow?: string;
  icon: React.ReactNode;
  accentColor?: string;
  infoItems?: FormModalInfoItem[];
  footer?: React.ReactNode;
  panelClassName?: string;
}

export function FormModal({
  children,
  open,
  onClose,
  title,
  subtitle,
  eyebrow = "Workspace record",
  icon,
  accentColor = "var(--accent)",
  infoItems = [],
  footer,
  panelClassName = "max-w-3xl",
}: FormModalProps) {
  return (
    <Modal open={open} onClose={onClose} panelClassName={panelClassName}>
      <div className="grid max-h-[calc(100vh-32px)] grid-cols-1 overflow-y-auto lg:grid-cols-[280px_1fr]">
        <div className="relative overflow-hidden p-6" style={{ background: `${accentColor}10` }}>
          <div
            className="absolute right-0 top-0 h-28 w-28 rounded-bl-[52px] opacity-[0.06]"
            style={{ background: accentColor }}
          />
          <div className="relative flex h-full flex-col justify-between gap-8">
            <div>
              <div className="inline-flex rounded-2xl bg-white p-3 shadow-sm" style={{ color: accentColor }}>
                {icon}
              </div>
              <h3 className="mt-5 text-2xl font-black leading-tight text-[var(--text-primary)]">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-[var(--text-secondary)]">{subtitle}</p>
            </div>

            {infoItems.length > 0 && (
              <div className="space-y-3 rounded-2xl border border-[var(--border-subtle)] bg-white/75 p-4">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)]">
                    {item.icon && <span style={{ color: accentColor }}>{item.icon}</span>}
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-[var(--surface)]">
          <div className="flex items-start justify-between border-b border-[var(--border-subtle)] p-6">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[var(--text-muted)]">{eyebrow}</div>
              <div className="mt-1 text-lg font-black text-[var(--text-primary)]">{title}</div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-[var(--surface-muted)] transition-colors text-[var(--text-muted)]"
              type="button"
              aria-label="Close modal"
            >
              <span aria-hidden="true" className="block text-lg leading-none">x</span>
            </button>
          </div>

          <div className="p-6">
            {children}
            {footer && <div className="mt-6 border-t border-[var(--border-subtle)] pt-5">{footer}</div>}
          </div>
        </div>
      </div>
    </Modal>
  );
}
